import type { PlaylistItem } from '../types/PlaylistItem';

interface PlayerFooterProps {
  currentTrack: PlaylistItem | null;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function getCurrentClock(): string {
  const now = new Date();
  return now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export default function PlayerFooter({
  currentTrack,
  isPlaying,
  currentTime,
  duration,
}: PlayerFooterProps) {
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const clock = getCurrentClock();

  return (
    <footer className="bg-neutral-900/90 backdrop-blur-sm border-t border-white/5">
      <div className="h-1.5 bg-neutral-800 relative">
        <div
          className="h-full bg-emerald-500 transition-all duration-300 relative"
          style={{ width: `${Math.min(progress, 100)}%` }}
        >
          <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-white shadow-[0_0_8px_rgba(255,255,255,0.4)]" />
        </div>
      </div>

      <div className="px-6 py-4 flex items-center gap-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="text-neutral-400 text-xs font-mono whitespace-nowrap">
            {formatTime(currentTime)}
          </div>
          <span className="text-neutral-700 text-xs">/</span>
          <div className="text-neutral-500 text-xs font-mono whitespace-nowrap">
            {formatTime(duration)}
          </div>
        </div>

        <div className="flex items-center gap-5">
          <button
            type="button"
            className="w-9 h-9 flex items-center justify-center text-neutral-400 hover:text-white transition-colors cursor-pointer"
            aria-label="Previous"
          >
            <i className="ri-skip-back-fill text-xl" />
          </button>

          <button
            type="button"
            className="w-14 h-14 rounded-full bg-white flex items-center justify-center hover:scale-105 transition-transform cursor-pointer shadow-lg shadow-black/30"
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            <i className={`${isPlaying ? 'ri-pause-fill' : 'ri-play-fill'} text-neutral-900 text-2xl`} />
          </button>

          <button
            type="button"
            className="w-9 h-9 flex items-center justify-center text-neutral-400 hover:text-white transition-colors cursor-pointer"
            aria-label="Next"
          >
            <i className="ri-skip-forward-fill text-xl" />
          </button>
        </div>

        <div className="flex-1 flex items-center justify-end gap-3">
          <button
            type="button"
            className="w-8 h-8 flex items-center justify-center text-neutral-400 hover:text-white transition-colors cursor-pointer"
            aria-label="Volume"
          >
            <i className="ri-volume-up-line text-lg" />
          </button>
          <span className="text-neutral-400 text-xs font-mono whitespace-nowrap">
            {clock}
          </span>
        </div>
      </div>
    </footer>
  );
}